/**
 * WikiSidebar — Wiki 게시판 좌측 페이지 트리 사이드바
 *
 * - 페이지 트리는 parent_post_id 기준 재귀
 * - 같은 형제(같은 parent_post_id)끼리는 tree_sort_order 로 정렬 (수동 정렬 가능)
 * - canManage 사용자에겐 GripVertical 드래그 핸들 노출 → dnd-kit 으로 reorder
 * - 핀(is_pinned) 은 시각 표식만 (정렬 영향 없음 — 매뉴얼 순서가 우선)
 *
 * Phase 1: 같은 부모 안 reorder 만 지원 (부모 변경은 게시글 수정 모달의 parent picker 로)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { cn } from '../../lib/utils';
import {
  FileText, ChevronRight, ChevronDown, Pin, FolderTree, Plus, X, GripVertical,
  ArrowUpDown, Check, Loader2,
} from 'lucide-react';
import { Post, canManageBoard } from '../../models/Board';
import { useBoardContext } from '../../context/BoardContext';
import { useResponsive } from '../../hooks/useResponsive';
import { useMyRole } from '../../context/WorkspacePermissionContext';
import { useWorkspaceSettings } from '../../context/WorkspaceSettingsContext';
import { My } from '../../services/My';
import { reorderWikiPages } from '../../services/BoardService';
import { toast } from '../../components/ui/use-toast';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface PageNode {
  post: Post;
  children: PageNode[];
}

/** posts 를 parent_post_id 기준 트리로 변환. 같은 부모 안 정렬은 tree_sort_order asc, then created_at asc. */
function buildPageTree(posts: Post[]): PageNode[] {
  const byParent = new Map<string | null, Post[]>();
  posts.forEach(p => {
    const key = p.parent_post_id ?? null;
    const arr = byParent.get(key) ?? [];
    arr.push(p);
    byParent.set(key, arr);
  });
  byParent.forEach(group => {
    group.sort((a, b) =>
      (a.tree_sort_order - b.tree_sort_order)
      || a.created_at.localeCompare(b.created_at)
    );
  });

  const seen = new Set<string>();
  function build(parentId: string | null): PageNode[] {
    const list = byParent.get(parentId) ?? [];
    return list
      .filter(p => !seen.has(p.id))
      .map(p => {
        seen.add(p.id);
        return { post: p, children: build(p.id) };
      });
  }
  return build(null);
}

/** 단일 페이지 행 — useSortable 로 드래그 가능 */
const PageTreeItem: React.FC<{
  node: PageNode;
  depth: number;
  selectedPostId: string | null;
  onPostClick: (post: Post) => void;
  canReorder: boolean;
}> = ({ node, depth, selectedPostId, onPostClick, canReorder }) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const isSelected = selectedPostId === node.post.id;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.post.id,
    disabled: !canReorder,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        data-state={isSelected ? 'on' : undefined}
        className={cn(
          'group/row flex items-center gap-0.5 pr-2 py-1 rounded-md text-label transition-colors',
          'text-[color-mix(in_srgb,rgb(var(--foreground))_75%,transparent)] hover:bg-muted',
          'data-[state=on]:bg-primary/10 data-[state=on]:text-primary data-[state=on]:font-medium',
        )}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        {/* 드래그 핸들 — canReorder 시에만 노출 (모바일/탭 friendly: hover 의존 안 함) */}
        {canReorder ? (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="shrink-0 size-3.5 rounded flex items-center justify-center text-muted-foreground/50 hover:text-foreground hover:bg-foreground/10 cursor-grab active:cursor-grabbing"
            title={t`Drag to reorder`}
            aria-label={t`Drag to reorder`}
          >
            <GripVertical className="size-2.5" />
          </button>
        ) : null}

        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
            className="shrink-0 p-0.5 rounded hover:bg-foreground/10 cursor-pointer"
            aria-label={expanded ? t`Collapse` : t`Expand`}
          >
            {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          </button>
        ) : null}
        <button
          onClick={() => onPostClick(node.post)}
          className="flex items-center gap-1.5 flex-1 min-w-0 text-left cursor-pointer py-0.5 ml-1"
        >
          <FileText className="size-3 shrink-0 opacity-55" />
          <span className="truncate">{node.post.title || t`Untitled`}</span>
          {node.post.is_pinned && (
            <Pin className="size-2.5 shrink-0 text-badge-caution fill-current" />
          )}
        </button>
      </div>
      {hasChildren && expanded && (
        <SortableGroup
          parentId={node.post.id}
          children={node.children}
          depth={depth + 1}
          selectedPostId={selectedPostId}
          onPostClick={onPostClick}
          canReorder={canReorder}
        />
      )}
    </div>
  );
};

/** 한 부모(parentId) 의 형제 children 을 SortableContext 로 감쌈.
 *  각 레벨이 자체 SortableContext 라 같은 부모 안에서만 reorder 가능 — Phase 1 의도. */
const SortableGroup: React.FC<{
  parentId: string | null;
  children: PageNode[];
  depth: number;
  selectedPostId: string | null;
  onPostClick: (post: Post) => void;
  canReorder: boolean;
}> = ({ parentId: _parentId, children, depth, selectedPostId, onPostClick, canReorder }) => (
  <SortableContext items={children.map(c => c.post.id)} strategy={verticalListSortingStrategy}>
    {children.map(child => (
      <PageTreeItem
        key={child.post.id}
        node={child}
        depth={depth}
        selectedPostId={selectedPostId}
        onPostClick={onPostClick}
        canReorder={canReorder}
      />
    ))}
  </SortableContext>
);

interface WikiSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  onNewPage?: () => void;
}

const WikiSidebar: React.FC<WikiSidebarProps> = ({ isOpen = false, onClose, onNewPage }) => {
  const { posts, setPosts, selectedPost, setSelectedPost, selectedBoard, refreshPosts } = useBoardContext();
  const myRole = useMyRole();
  const wsSettings = useWorkspaceSettings();
  const selectedPostId = selectedPost?.id ?? null;
  const tree = useMemo(() => buildPageTree(posts), [posts]);

  // 재정렬 권한 — admin 동등 (owner/admin/board admin_ids/workspace managers)
  const canReorderPermission = !!selectedBoard
    && canManageBoard(selectedBoard, myRole ?? 'guest', My.user?.id, wsSettings.board_manager_user_ids);

  // 순서 변경 모드 — 헤더 버튼으로 진입/저장/취소. 모드 안에서만 핸들 노출 + drag 가능
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderSnapshot, setReorderSnapshot] = useState<Post[] | null>(null);
  const [reorderSaving, setReorderSaving] = useState(false);
  const canReorder = canReorderPermission && reorderMode;

  const enterReorderMode = () => {
    setReorderSnapshot([...posts]);
    setReorderMode(true);
  };
  const cancelReorder = () => {
    if (reorderSnapshot) setPosts(reorderSnapshot);
    setReorderSnapshot(null);
    setReorderMode(false);
  };
  const saveReorder = async () => {
    if (!reorderSnapshot || reorderSaving) return;
    setReorderSaving(true);
    try {
      // 변경된 부모 그룹만 reorderWikiPages 호출
      const groupByParent = (list: Post[]) => {
        const m = new Map<string | null, Post[]>();
        list.forEach(p => {
          const key = p.parent_post_id ?? null;
          if (!m.has(key)) m.set(key, []);
          m.get(key)!.push(p);
        });
        m.forEach(group =>
          group.sort((a, b) =>
            (a.tree_sort_order - b.tree_sort_order) || a.created_at.localeCompare(b.created_at)
          ),
        );
        return m;
      };
      const currentGroups = groupByParent(posts);
      const snapshotGroups = groupByParent(reorderSnapshot);
      const tasks: Promise<void>[] = [];
      currentGroups.forEach((cur, parentKey) => {
        const snap = snapshotGroups.get(parentKey) ?? [];
        const curIds = cur.map(p => p.id).join(',');
        const snapIds = snap.map(p => p.id).join(',');
        if (curIds !== snapIds) {
          tasks.push(reorderWikiPages(cur.map(p => p.id)));
        }
      });
      if (tasks.length === 0) {
        // 변경 없음 — 그냥 종료
        setReorderMode(false);
        setReorderSnapshot(null);
        return;
      }
      await Promise.all(tasks);
      toast({ title: t`Order saved` });
      setReorderMode(false);
      setReorderSnapshot(null);
    } catch (err) {
      console.error('[WikiSidebar] 순서 저장 실패:', err);
      toast({ title: t`Failed to save`, variant: 'destructive' });
      if (selectedBoard) refreshPosts(selectedBoard.id);
    } finally {
      setReorderSaving(false);
    }
  };

  // 보드 전환 시 모드 자동 해제 (snapshot 잔존 방지)
  useEffect(() => {
    setReorderMode(false);
    setReorderSnapshot(null);
  }, [selectedBoard?.id]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // 드래그 종료 — 모드 안에서는 로컬 setPosts 만 (서버 저장은 '저장' 클릭 시 일괄)
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (!reorderMode) return; // 안전장치 — 모드 밖에선 드래그 발생 안 함
    const activeId = String(active.id);
    const overId = String(over.id);

    const activePost = posts.find(p => p.id === activeId);
    const overPost = posts.find(p => p.id === overId);
    if (!activePost || !overPost) return;
    if ((activePost.parent_post_id ?? null) !== (overPost.parent_post_id ?? null)) {
      toast({ title: t`You can only reorder within the same level` });
      return;
    }

    const parentId = activePost.parent_post_id ?? null;
    const siblings = posts
      .filter(p => (p.parent_post_id ?? null) === parentId)
      .sort((a, b) => (a.tree_sort_order - b.tree_sort_order) || a.created_at.localeCompare(b.created_at));
    const oldIdx = siblings.findIndex(s => s.id === activeId);
    const newIdx = siblings.findIndex(s => s.id === overId);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(siblings, oldIdx, newIdx);

    const reorderedMap = new Map(reordered.map((p, i) => [p.id, i]));
    setPosts(posts.map(p => {
      const idx = reorderedMap.get(p.id);
      return idx !== undefined ? { ...p, tree_sort_order: idx } : p;
    }));
  };

  const handlePostClick = (post: Post) => {
    setSelectedPost(post);
    onClose?.();
  };

  const { isMobile } = useResponsive();

  const content = (
    <div className="w-full h-full flex flex-col bg-[color-mix(in_srgb,rgb(var(--muted))_35%,rgb(var(--background)))]">
      {/* 좁은 폭(컨테이너 기준) 헤더 — 닫기 버튼 포함 */}
      <div className={cn('flex items-center justify-between gap-1.5 px-3 py-2 border-b border-border shrink-0', !isMobile && 'hidden')}>
        {reorderMode ? (
          <>
            <span className="inline-flex items-center gap-1.5 text-label font-semibold text-primary">
              <ArrowUpDown className="size-3.5" />
              <Trans>Reordering</Trans>
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={cancelReorder}
                disabled={reorderSaving}
                className="px-2 h-7 rounded-md text-meta font-medium text-muted-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-50"
              >
                <Trans>Cancel</Trans>
              </button>
              <button
                type="button"
                onClick={saveReorder}
                disabled={reorderSaving}
                className="inline-flex items-center gap-1 px-2.5 h-7 rounded-md text-meta font-semibold text-primary-foreground bg-primary hover:brightness-110 transition-all cursor-pointer disabled:opacity-60"
              >
                {reorderSaving ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
                <Trans>Save</Trans>
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="text-label font-semibold text-foreground"><Trans>Pages</Trans></span>
            <div className="flex items-center gap-1">
              {canReorderPermission && posts.length > 1 && (
                <button
                  type="button"
                  onClick={enterReorderMode}
                  className="size-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-muted transition-colors cursor-pointer"
                  aria-label={t`Reorder`}
                  title={t`Reorder`}
                >
                  <ArrowUpDown className="size-4" />
                </button>
              )}
              <button
                onClick={onClose}
                className="size-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                aria-label={t`Close`}
              >
                <X className="size-4" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* 넓은 폭(컨테이너 기준) 헤더 — 순서 변경 모드에선 [취소] / [저장] 버튼으로 변형 */}
      <div className={cn('items-center justify-between gap-1.5 px-3 pt-3 pb-2 shrink-0', isMobile ? 'hidden' : 'flex')}>
        {reorderMode ? (
          <>
            <span className="inline-flex items-center gap-1.5 text-meta-sm font-bold text-primary uppercase tracking-[0.08em]">
              <ArrowUpDown className="size-3" />
              <Trans>Reordering</Trans>
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={cancelReorder}
                disabled={reorderSaving}
                className="px-2 h-[22px] rounded text-meta-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-50"
                title={t`Cancel`}
              >
                <Trans>Cancel</Trans>
              </button>
              <button
                type="button"
                onClick={saveReorder}
                disabled={reorderSaving}
                className="inline-flex items-center gap-1 px-2 h-[22px] rounded text-meta-sm font-semibold text-primary-foreground bg-primary hover:brightness-110 transition-all cursor-pointer disabled:opacity-60"
                title={t`Save`}
              >
                {reorderSaving ? <Loader2 className="size-3 animate-spin" /> : <Check className="size-3" />}
                <Trans>Save</Trans>
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1.5 text-meta-sm font-bold text-muted-foreground uppercase tracking-[0.08em]">
              <FolderTree className="size-3" />
              <Trans>Pages</Trans>
            </span>
            <div className="flex items-center gap-0.5">
              {canReorderPermission && posts.length > 1 && (
                <button
                  type="button"
                  onClick={enterReorderMode}
                  className="size-[22px] rounded flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-muted transition-colors cursor-pointer"
                  aria-label={t`Reorder`}
                  title={t`Reorder`}
                >
                  <ArrowUpDown className="size-3" />
                </button>
              )}
              {onNewPage && (
                <button
                  onClick={onNewPage}
                  className="size-[22px] rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                  aria-label={t`New page`}
                  title={t`New page`}
                >
                  <Plus className="size-3" />
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* 트리 본문 — 전체를 DndContext 로 감싸고, 각 부모 레벨이 자체 SortableContext */}
      <div className="flex-1 px-1.5 pb-2 space-y-px overflow-y-auto">
        {tree.length === 0 ? (
          <p className="px-3 py-4 text-meta text-muted-foreground">
            <Trans>No pages yet</Trans>
          </p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableGroup
              parentId={null}
              children={tree}
              depth={0}
              selectedPostId={selectedPostId}
              onPostClick={handlePostClick}
              canReorder={canReorder}
            />
          </DndContext>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* 넓은 폭(컨테이너 기준): 좌측 고정 사이드바 */}
      {!isMobile && (
        <aside className="w-[240px] shrink-0 border-r border-border flex flex-col">
          {content}
        </aside>
      )}

      {/* 좁은 폭(컨테이너 기준, 채널 분할 패널 포함): 슬라이딩 drawer */}
      {isMobile && isOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-[2px]"
            onClick={onClose}
            aria-hidden
          />
          <aside className="fixed left-0 top-0 bottom-0 z-50 w-[280px] max-w-[80vw] border-r border-border shadow-xl animate-in slide-in-from-left duration-200">
            {content}
          </aside>
        </>
      )}
    </>
  );
};

export default WikiSidebar;
